import { keyframes } from "@emotion/react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { setIsLoading } from "src/slices/settings";
import { useDispatch, useSelector } from "src/store";
import { getDenied, subscribeDenied } from "src/utils/permissionDebugStore";
import NotFoundView from "src/views/errors/NotFoundView";
import NavBar from "./NavBar";
import TopBar from "./TopBar";

// Lazy — su bundle solo se descarga cuando se produce la primera denegación
const LazySecurityGuard = lazy(() => import("src/components/SecurityGuard"));

/**
 * Micro-bridge: suscribe al permissionDebugStore sin ningún overhead de MUI.
 * Solo renderiza el guardia (lazy) cuando hay al menos una denegación Y adminMode
 * está activo. El bundle de SecurityGuard nunca se descarga en sesiones normales.
 */
const SecurityGuardBridge = () => {
  const adminMode = useSelector((state) => state.settings.adminMode);
  const [hasDenied, setHasDenied] = useState(() => getDenied().length > 0);

  useEffect(() => {
    const unsub = subscribeDenied((list) => setHasDenied(list.length > 0));
    // Sincronizar con el estado actual por si ya hubo denegaciones antes del mount
    setHasDenied(getDenied().length > 0);
    return unsub;
  }, []);

  if (!adminMode || !hasDenied) return null;

  return (
    <Suspense fallback={null}>
      <LazySecurityGuard />
    </Suspense>
  );
};

// Subtle entrance for content
const contentFadeIn = keyframes`
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Loading message fade-in
const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(-6%); }
  100% { opacity: 1; transform: translateY(0); }
`;

// Gentle pulse for spinner aura
const pulse = keyframes`
  0% { transform: scale(0.95); opacity: 0.6; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.6; }
`;

const APP_BAR_HEIGHT = { xs: "56px", md: "64px" };

const DashboardLayout = ({ children }) => {
  const theme = useTheme();
  const upLg = useMediaQuery(theme.breakpoints.up("lg"));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { customerName } = useParams();
  const pathname = window.location.pathname;
  const isRegistrationPath = pathname.includes("account/registration");
  console.log("isRegistrationPath:", isRegistrationPath);
  const DRAWER_WIDTH = isRegistrationPath ? 0 : 276;

  const [isMobileNavOpen, setMobileNavOpen] = useState(false);
  const initialized = useRef(false);

  // Select only what we need to minimize rerenders
  const customerError = useSelector((state) => state.customers.customerError);
  const isLoading = useSelector((state) => state.settings.isLoading);
  const loadingMessage = useSelector((state) => state.settings.loadingMessage);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      dispatch(setIsLoading(false));
    }
  }, [dispatch]);

  const handleOpenMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const handleCloseMobileNav = useCallback(() => setMobileNavOpen(false), []);

  const rootSx = useMemo(
    () => ({
      position: "relative",
      display: "flex",
      width: "100%",
      height: "100%",
      overflow: "hidden",
      backgroundColor: theme.palette.background.dark,
      // Subtle gradient accent for a modern, tech look
      backgroundImage: `radial-gradient(1200px 200px at 80% -100px, ${alpha(
        theme.palette.primary.main,
        0.08,
      )}, transparent 60%)`,
    }),
    [theme],
  );

  const wrapperSx = useMemo(
    () => ({
      display: "flex",
      flex: "1 1 auto",
      overflow: "hidden",
      pt: APP_BAR_HEIGHT,
      transition: theme.transitions.create(["padding-left"], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
      ...(upLg && { pl: `${DRAWER_WIDTH}px` }),
    }),
    [theme, upLg],
  );

  const contentContainerSx = useMemo(
    () => ({
      display: "flex",
      flex: "1 1 auto",
      overflow: "hidden",
    }),
    [],
  );

  const contentSx = useMemo(
    () => ({
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      height: "100%",
      overflow: "auto",
      scrollBehavior: "smooth",
      overscrollBehavior: "contain",
      WebkitOverflowScrolling: "touch",
      backfaceVisibility: "hidden",
      willChange: "transform, opacity",
      animation: `${contentFadeIn} 420ms ${theme.transitions.easing.easeOut}`,
      px: { xs: 1.5, md: 3 },
      pb: { xs: 2, md: 3 },
      gap: { xs: 1.5, md: 2 },
      fontFamily: `'Inter', 'Roboto', 'Helvetica Neue', Arial, sans-serif`,
    }),
    [theme],
  );

  if (customerName && customerError) {
    return <NotFoundView message={customerError} />;
  }

  return (
    <Box sx={rootSx}>
      {/* App top bar */}
      <TopBar onMobileNavOpen={handleOpenMobileNav} />

      {/* Side navigation */}
      {isRegistrationPath ? null : (
        <NavBar
          openMobile={isMobileNavOpen}
          onMobileClose={handleCloseMobileNav}
        />
      )}

      {/* Guardia de Seguridad Digital — solo carga su bundle si hay permisos denegados */}
      <SecurityGuardBridge />

      {/* Floating AI assistant */}
      <Box
        aria-label={t("ai.assistant") || "AI Assistant"}
        sx={{
          position: "fixed",
          bottom: { xs: 12, md: 16 },
          right: { xs: 12, md: 16 },
          zIndex: theme.zIndex.drawer + 2,
          filter: "drop-shadow(0 6px 18px rgba(0,0,0,0.35))",
          transition: theme.transitions.create(["transform", "opacity"], {
            duration: theme.transitions.duration.shorter,
          }),
          "&:hover": { transform: "translateY(-2px)" },
        }}
      >
        {/* <AIChatBot role="chat" /> */}
      </Box>

      {/* Main wrapper */}
      <Box sx={wrapperSx}>
        <Box sx={contentContainerSx}>
          <Box component="main" sx={contentSx}>
            {children}
          </Box>
        </Box>
      </Box>

      {/* Loading overlay with tech glow - sin parpadeos */}
      <Backdrop
        open={Boolean(isLoading)}
        sx={{
          color: "#fff",
          zIndex: (t) => t.zIndex.modal + 2,
          backdropFilter: "blur(3px)",
          backgroundColor: (t) => alpha(t.palette.background.default, 0.6),
          alignItems: "center",
          justifyContent: "center",
          // Transiciones asimétricas: rápida apertura, cierre más lento
          transition: theme.transitions.create(["opacity", "backdrop-filter"], {
            duration: isLoading ? 150 : 400, // Abre rápido, cierra lento
            easing: isLoading
              ? theme.transitions.easing.easeOut
              : theme.transitions.easing.easeInOut,
          }),
        }}
        // Sin unmountOnExit para evitar parpadeos
        keepMounted={false}
      >
        <Box
          sx={{
            position: "relative",
            display: "grid",
            placeItems: "center",
            p: { xs: 2, md: 3 },
            borderRadius: 2,
            // Transición del contenido
            transition: theme.transitions.create(["opacity", "transform"], {
              duration: 300,
              easing: theme.transitions.easing.easeOut,
            }),
            opacity: isLoading ? 1 : 0,
            transform: isLoading ? "scale(1)" : "scale(0.95)",
          }}
        >
          {/* Glow ring */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              width: { xs: 120, md: 160 },
              height: { xs: 120, md: 160 },
              borderRadius: "50%",
              background: (t) =>
                `radial-gradient(closest-side, ${alpha(
                  t.palette.primary.light,
                  0.35,
                )}, transparent 70%)`,
              animation: `${pulse} 2.6s ease-in-out infinite`,
              filter: "blur(2px)",
            }}
          />
          <CircularProgress
            thickness={4}
            size={90}
            sx={{
              color: "white",
              position: "relative",
              zIndex: 1,
              filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.35))",
            }}
          />
          <Typography
            variant="h5"
            component="div"
            aria-live="polite"
            key={loadingMessage} // Forzar re-render suave en cambio de mensaje
            sx={{
              mt: 3,
              px: 2,
              textAlign: "center",
              zIndex: 1,
              color: "white",
              fontWeight: 600,
              fontFamily: `'Inter', 'Roboto', 'Helvetica Neue', Arial, sans-serif`,
              fontSize: { xs: "1.1rem", md: "1.35rem" },
              letterSpacing: { xs: 0.2, md: 0.4 },
              textShadow: "0 1px 2px rgba(0,0,0,0.35)",
              // Transición suave del texto
              transition: theme.transitions.create(["opacity"], {
                duration: 250,
                easing: theme.transitions.easing.easeInOut,
              }),
            }}
          >
            {loadingMessage || t("loading")}
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

DashboardLayout.propTypes = {
  children: PropTypes.node,
};

export default DashboardLayout;
